import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import futuresContractsService from "./futuresContracts.service";
import IsLoaded from "common/isLoaded";
import FuturesPriceUpdate from "./futuresPriceUpdate";

type RealTimeSnapshot = IsLoaded<FuturesPriceUpdate> & { refresh: () => void };

export default function useRealTimeSnapshot(futuresContractId: number, shouldLoad: boolean): RealTimeSnapshot {
    const futuresContractIdRef = useRef(futuresContractId);
    futuresContractIdRef.current = futuresContractId;

    // keep isLoaded and data in a single combined state, so they can be set together at the same time
    // (React pre-v18 does not automatically batch multiple setState calls, so we need to keep them together to prevent extra renders with partially updated state)
    const [isLoadedAndData, setIsLoadedAndData] = useState<[boolean, FuturesPriceUpdate | null]>([false, null]);
    const [isLoaded, data] = isLoadedAndData;

    const updatePrice = useCallback(async function updatePrice(force: boolean = false) {
        if (!shouldLoad && !force)
            return;
        if (futuresContractId == null)
            return;
        const result = await futuresContractsService.getRealTimeSnapshot(futuresContractId);
        // don't save result if the requested futuresContractId (captured by closure) is not the same as the most recent futuresContractId (stored in ref)
        // this prevents a race condition where data for the previous futures contract loads slower and overwrites data for the current futures contract
        if (futuresContractId === futuresContractIdRef.current) {
            const loaded = result != null;
            setIsLoadedAndData([loaded, result]);
        }
    }, [shouldLoad, futuresContractId]);

    const refresh = useCallback(() => updatePrice(true), [updatePrice]);

    // automatically update price when function changes (ie shouldLoad or futuresContractId changed)
    useEffect(() => {
        updatePrice();
    }, [updatePrice]);

    return useMemo(() => ({ isLoaded, data, refresh }), [isLoaded, data, refresh]);
}